import { Component } from '@theme/component';

/**
 * A custom element that manages shipping date display and updates.
 *
 * @typedef {object} ShippingDateRefs
 * @property {HTMLElement} deliveryDateRange - The element displaying the date range.
 * @property {HTMLElement} freeShippingWrapper - The element wrapping the free shipping indicator.
 * @property {HTMLElement} freeShippingMessage - The element displaying the free shipping message.
 *
 * @extends Component<ShippingDateRefs>
 */
class ShippingDateComponent extends Component {
  requiredRefs = ['deliveryDateRange'];
  optionalRefs = ['freeShippingWrapper', 'freeShippingMessage'];

  /** @type {Array<{pattern: string, days: number}>} */
  #shippingConfig = [];

  /** @type {number} */
  #defaultDeliveryDays = 2;

  /** @type {number} */
  #cutoffHour = 15;

  /** @type {Array<Object>} */
  #productVariants = [];

  /** @type {number} */
  #freeShippingThreshold = 0;

  /** @type {number} */
  #currentVariantPrice = 0;

  connectedCallback() {
    super.connectedCallback();

    // Parse configuration from dataset with error handling
    try {
      this.#shippingConfig = JSON.parse(this.dataset.shippingConfig || '[]');
    } catch (error) {
      console.error('Error parsing shipping config:', this.dataset.shippingConfig);
      console.error('Parse error:', error);
      this.#shippingConfig = [];
    }

    this.#defaultDeliveryDays = parseInt(this.dataset.defaultDeliveryDays || '2');
    this.#cutoffHour = parseInt(this.dataset.cutoffHour || '15');
    this.#freeShippingThreshold = parseInt(this.dataset.freeShippingThreshold || '0');
    this.#currentVariantPrice = parseInt(this.dataset.variantPrice || '0');

    try {
      this.#productVariants = JSON.parse(this.dataset.productVariants || '[]');
    } catch (error) {
      console.error('Error parsing product variants:', this.dataset.productVariants);
      console.error('Parse error:', error);
      this.#productVariants = [];
    }

    // Initial render with current variant
    const initialSku = this.dataset.initialSku || '';
    this.updateDisplay(initialSku);
    this.updateFreeShippingIndicator();

    // Listen for Horizon theme's VariantUpdateEvent (bubbles up from variant-picker)
    document.addEventListener('variant:update', this.#handleVariantUpdateEvent);

    // Listen for form input changes (fallback for non-Horizon setups)
    const variantSelects = document.querySelectorAll('select[name="id"], input[name="id"]');
    variantSelects.forEach(select => {
      select.addEventListener('change', this.#handleVariantChange);
    });

    // Listen for quantity changes
    const quantityInput = document.querySelector('input[name="quantity"]');
    if (quantityInput) {
      quantityInput.addEventListener('change', this.#handleQuantityChange);
      quantityInput.addEventListener('input', this.#handleQuantityChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up event listeners
    document.removeEventListener('variant:update', this.#handleVariantUpdateEvent);

    const variantSelects = document.querySelectorAll('select[name="id"], input[name="id"]');
    variantSelects.forEach(select => {
      select.removeEventListener('change', this.#handleVariantChange);
    });

    const quantityInput = document.querySelector('input[name="quantity"]');
    if (quantityInput) {
      quantityInput.removeEventListener('change', this.#handleQuantityChange);
      quantityInput.removeEventListener('input', this.#handleQuantityChange);
    }
  }

  /**
   * Handles Horizon theme's VariantUpdateEvent
   * @param {CustomEvent} event - The variant:update event with variant data in detail.resource
   */
  #handleVariantUpdateEvent = (event) => {
    const variant = event.detail?.resource;
    if (variant) {
      this.#currentVariantPrice = variant.price;
      this.updateDisplay(variant.sku || '');
      this.updateFreeShippingIndicator();
    }
  };

  /**
   * Handles variant change from form inputs
   * @param {Event} event
   */
  #handleVariantChange = (event) => {
    const variantId = parseInt(event.target.value);
    const variant = this.#productVariants.find(v => v.id === variantId);
    if (variant) {
      this.#currentVariantPrice = variant.price;
      this.updateDisplay(variant.sku);
      this.updateFreeShippingIndicator();
    }
  };

  /**
   * Handles quantity change from form inputs
   * @param {Event} event
   */
  #handleQuantityChange = (event) => {
    this.updateFreeShippingIndicator();
  };

  /**
   * Gets shipping config based on SKU pattern matching
   * @param {string} sku
   * @returns {{days: number, cutoff: string}}
   */
  #getShippingConfigForSku(sku) {
    const defaultConfig = {
      days: this.#defaultDeliveryDays,
      cutoff: '15:00'
    };

    if (!sku) return defaultConfig;

    const skuLower = sku.toLowerCase();

    for (const config of this.#shippingConfig) {
      const patternLower = config.pattern.toLowerCase();
      if (skuLower.includes(patternLower)) {
        return { days: config.days, cutoff: config.cutoff };
      }
    }
    return defaultConfig;
  }

  /**
   * Adds production days (Monday-Friday only)
   * The start day counts as day 1
   * @param {Date} start
   * @param {number} days
   * @returns {Date}
   */
  #addProductionDays(start, days) {
    if (days === 0) return new Date(start);

    const d = new Date(start);
    let counted = 1; // Start day is day 1

    while (counted < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay(); // 0=Sunday, 6=Saturday
      if (day !== 0 && day !== 6) {
        counted++; // Only count Mon-Fri
      }
    }
    return d;
  }

  /**
   * Gets next delivery day (Monday-Saturday, skip Sunday)
   * @param {Date} start
   * @returns {Date}
   */
  #getNextDeliveryDay(start) {
    const d = new Date(start);
    d.setDate(d.getDate() + 1);
    // If it's Sunday, move to Monday
    if (d.getDay() === 0) {
      d.setDate(d.getDate() + 1);
    }
    return d;
  }

  /**
   * Gets next production day (Monday-Friday)
   * @param {Date} start
   * @returns {Date}
   */
  #getNextProductionDay(start) {
    const d = new Date(start);
    d.setDate(d.getDate() + 1); // Move to tomorrow first
    const day = d.getDay();
    // If it lands on weekend, move to Monday
    if (day === 0) { // Sunday -> Monday
      d.setDate(d.getDate() + 1);
    } else if (day === 6) { // Saturday -> Monday
      d.setDate(d.getDate() + 2);
    }
    return d;
  }

  /**
   * Parses cutoff time string (HH:MM) to hours and minutes
   * @param {string} cutoffTime
   * @returns {{hours: number, minutes: number}}
   */
  #parseCutoffTime(cutoffTime) {
    const [hours, minutes] = cutoffTime.split(':').map(Number);
    return { hours, minutes };
  }

  /**
   * Formats date in Dutch locale
   * @param {Date} date
   * @returns {string}
   */
  #formatDate(date) {
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
  }

  /**
   * Calculates delivery date
   * @param {number} productionDays
   * @param {string} cutoffTime
   * @returns {string}
   */
  #calculateDeliveryDate(productionDays, cutoffTime) {
    // Current time in Amsterdam
    const now = new Date();
    const amsTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Amsterdam' }));
    const currentDay = amsTime.getDay(); // 0=Sunday, 6=Saturday
    const currentHour = amsTime.getHours();
    const currentMinute = amsTime.getMinutes();

    const cutoff = this.#parseCutoffTime(cutoffTime);
    const currentTimeMinutes = currentHour * 60 + currentMinute;
    const cutoffTimeMinutes = cutoff.hours * 60 + cutoff.minutes;
    const isBeforeCutoff = currentTimeMinutes < cutoffTimeMinutes;


    let productionStartDate;

    // Determine when production starts
    if (currentDay >= 1 && currentDay <= 5 && isBeforeCutoff) {
      // Monday-Friday before cutoff: production starts today
      productionStartDate = new Date(amsTime);
    } else {
      // After cutoff or weekend: production starts next production day
      productionStartDate = this.#getNextProductionDay(amsTime);
    }


    // Add production days (Mon-Fri only) - the config "days" represents production time, not total delivery time
    const productionEndDate = this.#addProductionDays(productionStartDate, productionDays);

    // Ship same day production ends, delivery is next delivery day (Mon-Sat, skip Sunday)
    let deliveryDate = this.#getNextDeliveryDay(productionEndDate);

    // Check if this is a German (.de) domain and add +1 day
    const canonicalUrl = document.querySelector('link[rel="canonical"]')?.getAttribute('href') || window.location.href;
    if (canonicalUrl.includes('.de')) {
      deliveryDate = this.#getNextDeliveryDay(deliveryDate);
    }

    return this.#formatDate(deliveryDate);
  }

  /**
   * Updates the display with new delivery date based on SKU
   * @param {string} sku
   */
  updateDisplay(sku) {
    const config = this.#getShippingConfigForSku(sku);
    const deliveryDate = this.#calculateDeliveryDate(config.days, config.cutoff);

    if (this.refs.deliveryDateRange) {
      this.refs.deliveryDateRange.textContent = deliveryDate;
    } else {
    }
  }

  /**
   * Updates shipping date based on variant object
   * @param {Object} variant - Variant object with SKU
   */
  updateShippingDate(variant) {
    if (variant && variant.sku) {
      this.#currentVariantPrice = variant.price;
      this.updateDisplay(variant.sku);
      this.updateFreeShippingIndicator();
    }
  }

  /**
   * Updates the free shipping indicator visibility
   */
  updateFreeShippingIndicator() {
    if (!this.refs.freeShippingWrapper) return;

    const quantityInput = document.querySelector('input[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
    const total = this.#currentVariantPrice * quantity;

    if (this.#freeShippingThreshold > 0 && total >= this.#freeShippingThreshold) {
      this.refs.freeShippingWrapper.style.display = 'flex';
    } else {
      this.refs.freeShippingWrapper.style.display = 'none';
    }
  }
}

if (!customElements.get('shipping-date-component')) {
  customElements.define('shipping-date-component', ShippingDateComponent);
}
